import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

const WISHLIST_CHANGE_EVENT = 'wishlist:change';

/**
 * @typedef {Object} WishlistList
 * @property {string} id
 * @property {string} name
 * @property {string[]} handles
 */

/**
 * @typedef {Object} WishlistData
 * @property {number} version
 * @property {WishlistList[]} lists
 */

/**
 * Manages multi-list wishlist state in localStorage.
 */
export class Wishlist {
  static #STORAGE_KEY = 'wishlists';

  /** @returns {WishlistData} */
  static #getData() {
    const raw = localStorage.getItem(this.#STORAGE_KEY);
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch {
        // Corrupted data — reset
      }
    }
    const data = { version: 1, lists: [{ id: 'default', name: 'Favorites', handles: [] }] };
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(data));
    return data;
  }

  /** @param {WishlistData} data */
  static #setData(data) {
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(data));
    this.#dispatch();
  }

  /** @returns {string} */
  static #generateId() {
    return Math.random().toString(36).slice(2, 8);
  }

  static #dispatch() {
    document.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT));
  }

  // ── List management ──

  /** @returns {WishlistList[]} */
  static getLists() {
    return this.#getData().lists;
  }

  /**
   * @param {string} name
   * @returns {WishlistList}
   */
  static createList(name) {
    const data = this.#getData();
    const list = { id: this.#generateId(), name, handles: [] };
    data.lists.push(list);
    this.#setData(data);
    return list;
  }

  /** @param {string} listId */
  static deleteList(listId) {
    if (listId === 'default') return;
    const data = this.#getData();
    data.lists = data.lists.filter((l) => l.id !== listId);
    this.#setData(data);
  }

  /**
   * @param {string} listId
   * @param {string} name
   */
  static renameList(listId, name) {
    const data = this.#getData();
    const list = data.lists.find((l) => l.id === listId);
    if (list) {
      list.name = name;
      this.#setData(data);
    }
  }

  // ── Product membership ──

  /**
   * @param {string} handle
   * @returns {string[]} List IDs containing the product
   */
  static getListsForProduct(handle) {
    return this.#getData().lists.filter((l) => l.handles.includes(handle)).map((l) => l.id);
  }

  /**
   * @param {string} handle
   * @param {string} listId
   */
  static addProductToList(handle, listId) {
    const data = this.#getData();
    const list = data.lists.find((l) => l.id === listId);
    if (list && !list.handles.includes(handle)) {
      list.handles.push(handle);
      this.#setData(data);
    }
  }

  /**
   * @param {string} handle
   * @param {string} listId
   */
  static removeProductFromList(handle, listId) {
    const data = this.#getData();
    const list = data.lists.find((l) => l.id === listId);
    if (list) {
      list.handles = list.handles.filter((h) => h !== handle);
      this.#setData(data);
    }
  }

  /**
   * @param {string} handle
   * @param {string} listId
   * @returns {boolean} New state (true = added)
   */
  static toggleProductInList(handle, listId) {
    const data = this.#getData();
    const list = data.lists.find((l) => l.id === listId);
    if (!list) return false;

    if (list.handles.includes(handle)) {
      list.handles = list.handles.filter((h) => h !== handle);
      this.#setData(data);
      return false;
    }

    list.handles.push(handle);
    this.#setData(data);
    return true;
  }

  // ── Cross-list queries ──

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  static hasProduct(handle) {
    return this.#getData().lists.some((l) => l.handles.includes(handle));
  }

  /** @returns {string[]} Deduplicated union of all handles */
  static getAllProducts() {
    const all = this.#getData().lists.flatMap((l) => l.handles);
    return [...new Set(all)];
  }

  /** @returns {number} */
  static get count() {
    return this.getAllProducts().length;
  }
}

/**
 * Heart toggle button for adding/removing products from wishlists.
 * Opens the wishlist dialog on click.
 */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#syncState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#syncState);
  }

  get handle() {
    return this.dataset.productHandle;
  }

  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.handle) {
      console.warn('[wishlist-button] No product handle');
      return;
    }

    /** @type {WishlistDialog | null} */
    const dialog = document.querySelector('wishlist-dialog');
    if (!dialog) {
      console.warn('[wishlist-button] wishlist-dialog element not found in DOM');
      return;
    }

    console.log('[wishlist-button] Opening dialog for:', this.handle);
    dialog.showForProduct(this.handle);
  };

  #syncState = () => {
    if (!this.handle) return;
    const isWishlisted = Wishlist.hasProduct(this.handle);
    const button = this.querySelector('button');
    if (button) {
      button.setAttribute('aria-pressed', String(isWishlisted));
    }
    this.classList.toggle('wishlist-button--active', isWishlisted);
  };
}

/**
 * Dialog for selecting which wishlists to add a product to.
 *
 * @typedef {Object} WishlistDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLDivElement} listContainer
 * @property {HTMLInputElement} newListInput
 * @property {HTMLButtonElement} newListButton
 * @property {HTMLDivElement} newListForm
 * @property {HTMLButtonElement} newListToggle
 */
class WishlistDialog extends DialogComponent {
  /** @type {string | null} */
  #currentHandle = null;

  /** @param {string} handle */
  showForProduct(handle) {
    console.log('[wishlist-dialog] showForProduct:', handle, 'dialog open:', this.refs.dialog?.open);
    this.#currentHandle = handle;
    this.#collapseNewListForm();
    this.#render();
    this.showDialog();
    console.log('[wishlist-dialog] After showDialog, dialog open:', this.refs.dialog?.open);
  }

  #collapseNewListForm() {
    const { newListForm, newListToggle, newListInput } = this.refs;
    newListForm?.setAttribute('hidden', '');
    newListToggle?.setAttribute('aria-expanded', 'false');
    if (newListInput) newListInput.value = '';
  }

  toggleNewList = () => {
    const { newListForm, newListToggle, newListInput } = this.refs;
    if (!newListForm || !newListToggle) return;

    const isOpen = newListToggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      newListForm.setAttribute('hidden', '');
      newListToggle.setAttribute('aria-expanded', 'false');
    } else {
      newListForm.removeAttribute('hidden');
      newListToggle.setAttribute('aria-expanded', 'true');
      newListInput?.focus();
    }
  };

  #render() {
    const { listContainer } = this.refs;
    if (!listContainer || !this.#currentHandle) return;

    const lists = Wishlist.getLists();
    const productLists = Wishlist.getListsForProduct(this.#currentHandle);

    listContainer.innerHTML = '';

    for (const list of lists) {
      const checked = productLists.includes(list.id);
      const id = `wishlist-cb-${list.id}`;

      const row = document.createElement('div');
      row.className = 'wishlist-dialog__list-row';

      row.innerHTML = `
        <label class="wishlist-dialog__list-label" for="${id}">
          <input
            type="checkbox"
            id="${id}"
            data-list-id="${list.id}"
            on:change="/handleCheckboxChange"
            ${checked ? 'checked' : ''}
          >
          <span class="wishlist-dialog__list-name">${list.name}</span>
        </label>
      `;

      listContainer.appendChild(row);
    }
  }

  /** @param {Event} event */
  handleCheckboxChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const listId = input.dataset.listId;
    if (!listId || !this.#currentHandle) return;

    if (input.checked) {
      Wishlist.addProductToList(this.#currentHandle, listId);
    } else {
      Wishlist.removeProductFromList(this.#currentHandle, listId);
    }
  };

  createNewList = () => {
    const { newListInput } = this.refs;
    if (!newListInput) return;

    const name = newListInput.value.trim();
    if (!name) return;

    const list = Wishlist.createList(name);
    if (this.#currentHandle) {
      Wishlist.addProductToList(this.#currentHandle, list.id);
    }

    this.#collapseNewListForm();
    this.#render();
  };

  /** @param {KeyboardEvent} event */
  handleInputKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.createNewList();
    }
  };
}

/**
 * Displays filled heart in the header when wishlist has items.
 */
class WishlistCount extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#sync);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#sync);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) {
      this.#sync();
    }
  };

  #sync = () => {
    this.classList.toggle('wishlist-count--active', Wishlist.count > 0);
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-dialog')) {
  customElements.define('wishlist-dialog', WishlistDialog);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
